import React, { useState, useEffect, useCallback } from 'react';
import Card from '../reports/Card';
import InfoItem from '../reports/InfoItem';
import TimeSeriesChart from '../reports/TimeSeriesChart';
import usePagination from '../utils/usePagination';
import { formatLocalDateTime } from '../utils/formatDateTime';
import telemetryService from '../../services/telemetryService';
import '../assets/AllTabsCommon.css';
import '../assets/Home.css';
import '../assets/RemoteDebugSessionTabs.css';

const SESSIONS_PER_PAGE = 10;

function RTTLive() {
  const [sessions, setSessions]               = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [searchTerm, setSearchTerm]           = useState('');
  const [currentPage, setCurrentPage]         = useState(1);
  const [expandedId, setExpandedId]           = useState(null);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const data = await telemetryService.getMonitorableSessions();
      setSessions(Array.isArray(data) ? data : []);
    } catch (_) {
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const handleRowClick = (sessionId) => {
    setExpandedId((prev) => (prev === sessionId ? null : sessionId));
  };

  const filteredSessions = sessions.filter((s) =>
    !searchTerm ||
    (s.systemId  || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.sessionId || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const { pageItems: sessionPage, renderPageNumbers } = usePagination(
    filteredSessions, currentPage, setCurrentPage, SESSIONS_PER_PAGE
  );

  return (
    <div className="data-table-tab-container">
      {/* ── Search bar ────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div className="search-container" style={{ margin: 0, maxWidth: 320 }}>
          <input
            type="text"
            placeholder="Search by System ID or Session ID…"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
        </div>
        <button
          className="nd-refresh-btn"
          onClick={loadSessions}
          disabled={sessionsLoading}
          title="Refresh session list"
        >
          {sessionsLoading ? '⏳' : '↻'}
        </button>
      </div>

      {/* ── Sessions table ────────────────────────────────────────────── */}
      {sessionsLoading ? (
        <div className="card rt-card">
          <p className="rt-text-muted" style={{ padding: '24px 0', textAlign: 'center' }}>
            Loading sessions…
          </p>
        </div>
      ) : filteredSessions.length === 0 ? (
        <div className="card rt-card">
          <p className="rt-text-muted" style={{ padding: '24px 0', textAlign: 'center' }}>
            {sessions.length === 0
              ? 'No active sessions found. Start a session in Real-Time Device Management.'
              : 'No sessions match your search.'}
          </p>
        </div>
      ) : (
        <>
          <div className="boot-kpi-table-container">
            <div className="boot-kpi-table-wrapper">
              <table className="boot-kpi-table">
                <thead>
                  <tr>
                    <th style={{ width: '30%' }}>System ID</th>
                    <th style={{ width: '34%' }}>Session ID</th>
                    <th style={{ width: '22%' }}>Started</th>
                    <th style={{ width: '8%'  }}>Duration</th>
                    <th style={{ width: '6%'  }}></th>
                  </tr>
                </thead>
                <tbody>
                  {sessionPage.map((s) => (
                    <React.Fragment key={s.sessionId}>
                      <tr
                        className={`nd-sv-row${expandedId === s.sessionId ? ' expanded-row' : ''}`}
                        onClick={() => handleRowClick(s.sessionId)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="boot-kpi-table-cell-kpi nd-system-id" title={s.systemId}>
                          {s.systemId}
                        </td>
                        <td className="boot-kpi-table-cell" style={{ fontSize: 12, color: '#475569' }} title={s.sessionId}>
                          {s.sessionId}
                        </td>
                        <td className="boot-kpi-table-cell" style={{ fontSize: 12, color: '#475569' }}>
                          {s.startTime ? formatLocalDateTime(s.startTime) : '—'}
                        </td>
                        <td className="boot-kpi-table-cell" style={{ fontSize: 12, color: '#475569' }}>
                          {s.durationMins ? `${s.durationMins} min` : '—'}
                        </td>
                        <td className="boot-kpi-table-cell" style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8' }}>
                          {expandedId === s.sessionId ? '▲' : '▼'}
                        </td>
                      </tr>

                      {expandedId === s.sessionId && (
                        <tr className="expanded-details-row">
                          <td colSpan={5}>
                            <div className="expanded-row-content">
                              <SessionLiveTelemetry session={s} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {filteredSessions.length > SESSIONS_PER_PAGE && (
            <div className="pagination-controls">{renderPageNumbers()}</div>
          )}
        </>
      )}
    </div>
  );
}

// ── Inline live telemetry panel (mounted when a row is expanded) ─────────────

function SessionLiveTelemetry({ session }) {
  const [telemetry, setTelemetry]   = useState(null);
  const [loading, setLoading]       = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await telemetryService.getLiveTelemetry(session.sessionId, session.systemId);
      setTelemetry(data);
      if (data) {
        setLastUpdated(
          new Date().toLocaleTimeString(undefined, {
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
          })
        );
      }
    } catch (_) {
      setTelemetry(null);
    } finally {
      setLoading(false);
    }
  }, [session.sessionId, session.systemId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: '16px 8px' }}>
      {/* ── Header row with Live indicator + Refresh ────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        {telemetry && (
          <span className="status-connected">● Live</span>
        )}
        {lastUpdated && (
          <span className="rt-text-muted" style={{ fontSize: 12 }}>
            Last updated: {lastUpdated}
          </span>
        )}
        {loading && (
          <span className="rt-text-muted" style={{ fontSize: 12 }}>
            Refreshing…
          </span>
        )}
        <button
          className="rtt-refresh-btn"
          onClick={load}
          disabled={loading}
          title="Refresh telemetry"
        >
          {loading ? '⏳ Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      {loading && !telemetry && (
        <p className="rt-text-muted" style={{ padding: '16px 0', textAlign: 'center' }}>
          Loading telemetry…
        </p>
      )}

      {!loading && !telemetry && (
        <p className="rt-text-muted" style={{ padding: '16px 0', textAlign: 'center' }}>
          No telemetry data available for this session yet. Data will appear once the device starts reporting.
        </p>
      )}

      {telemetry && (
        <>
          {/* ── Throughput ──────────────────────────────────────────── */}
          <Card title="Throughput Performance" collapsible>
            <RefreshLabel label={telemetry.throughput.refreshLabel} />
            <div className="rt-detail-grid-2" style={{ marginBottom: 12 }}>
              <InfoItem label="UL" value={`${telemetry.throughput.ulMbps} Mbps`} />
              <InfoItem label="DL" value={`${telemetry.throughput.dlMbps} Mbps`} />
              <InfoItem label="RTT" value={`${telemetry.throughput.rttMs} ms`} />
              <InfoItem label="Jitter (UDP)" value={`${telemetry.throughput.jitterMs} ms`} />
              <InfoItem label="Packet Loss (UDP)" value={`${telemetry.throughput.packetLossPct}%`} />
              <InfoItem label="Test Type" value={telemetry.throughput.testType} />
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <TimeSeriesChart
                  title="UL Mbps"
                  series={[{ name: 'UL', color: '#9b59b6', data: telemetry.throughput.ulSeries }]}
                  yAxisLabel="Mbps"
                  timeRange="5m"
                />
              </div>
              <div style={{ flex: 1 }}>
                <TimeSeriesChart
                  title="DL Mbps"
                  series={[{ name: 'DL', color: '#2ecc71', data: telemetry.throughput.dlSeries }]}
                  yAxisLabel="Mbps"
                  timeRange="5m"
                />
              </div>
            </div>
          </Card>

          {/* ── Signal Quality ──────────────────────────────────────── */}
          <Card title="Signal Quality" collapsible>
            <RefreshLabel label={telemetry.signal.refreshLabel} />
            <div className="rt-detail-grid-2">
              <InfoItem label="RSRP — LTE"  value={telemetry.signal.rsrp.lte  != null ? `${telemetry.signal.rsrp.lte} dBm`  : 'N/A'} />
              <InfoItem label="RSRP — NR5G" value={telemetry.signal.rsrp.nr5g != null ? `${telemetry.signal.rsrp.nr5g} dBm` : 'N/A'} />
              <InfoItem label="RSRQ — LTE"  value={telemetry.signal.rsrq.lte  != null ? `${telemetry.signal.rsrq.lte} dB`   : 'N/A'} />
              <InfoItem label="RSRQ — NR5G" value={telemetry.signal.rsrq.nr5g != null ? `${telemetry.signal.rsrq.nr5g} dB`  : 'N/A'} />
              <InfoItem label="SINR — LTE"  value={telemetry.signal.sinr.lte  != null ? `${telemetry.signal.sinr.lte} dB`   : 'N/A'} />
              <InfoItem label="SINR — NR5G" value={telemetry.signal.sinr.nr5g != null ? `${telemetry.signal.sinr.nr5g} dB`  : 'N/A'} />
            </div>
          </Card>

          {/* ── Serving Cell ────────────────────────────────────────── */}
          <Card
            title={
              <>
                Serving Cell Metrics{' '}
                {telemetry.servingCell.rat && (
                  <span className="rt-chip rt-chip-blue" style={{ fontSize: 12, verticalAlign: 'middle' }}>
                    {telemetry.servingCell.rat}
                  </span>
                )}
              </>
            }
            collapsible
          >
            <RefreshLabel label={telemetry.servingCell.refreshLabel} />
            <div className="rt-detail-grid-2">
              <InfoItem label="Cell ID (CGI)" value={telemetry.servingCell.cellId} />
              <InfoItem label="PCI"           value={telemetry.servingCell.pci} />
              {telemetry.servingCell.earfcn != null && <InfoItem label="EARFCN" value={telemetry.servingCell.earfcn} />}
              {telemetry.servingCell.arfcn  != null && <InfoItem label="ARFCN"  value={telemetry.servingCell.arfcn} />}
              <InfoItem label="Bandwidth" value={telemetry.servingCell.bandwidth} />
              <InfoItem label="Band"      value={telemetry.servingCell.band} />
              <InfoItem label="TAC"       value={telemetry.servingCell.tac} />
              <InfoItem label="MCC-MNC"   value={telemetry.servingCell.mccMnc} />
              <InfoItem label="RSRP" value={telemetry.servingCell.rsrp != null ? `${telemetry.servingCell.rsrp} dBm` : 'N/A'} />
              <InfoItem label="RSRQ" value={telemetry.servingCell.rsrq != null ? `${telemetry.servingCell.rsrq} dB`  : 'N/A'} />
              <InfoItem label="SINR" value={telemetry.servingCell.sinr != null ? `${telemetry.servingCell.sinr} dB`  : 'N/A'} />
              <InfoItem label="TX Power" value={telemetry.servingCell.txPower} />
            </div>
          </Card>

          {/* ── Neighbor Cells ──────────────────────────────────────── */}
          <Card title="Neighbor Cell Metrics" collapsible>
            <RefreshLabel label={telemetry.neighborCells.refreshLabel} />
            <h4 style={{ marginTop: 8, marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
              LTE Neighbor Cells ({telemetry.neighborCells.lte.length})
            </h4>
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th><th>ARFCN</th><th>PCI</th><th>CGI</th>
                    <th>PLMN</th><th>RSRP (dBm)</th><th>RSRQ (dB)</th>
                  </tr>
                </thead>
                <tbody>
                  {telemetry.neighborCells.lte.length === 0 ? (
                    <tr><td colSpan={7} className="table-empty">No LTE neighbor cells</td></tr>
                  ) : (
                    telemetry.neighborCells.lte.map((cell) => (
                      <tr key={cell.num}>
                        <td>{cell.num}</td><td>{cell.arfcn}</td><td>{cell.pci}</td>
                        <td>{cell.cgi || '—'}</td><td>{cell.plmn}</td>
                        <td>{cell.rsrp}</td><td>{cell.rsrq}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <h4 style={{ marginTop: 16, marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
              NR5G Neighbor Cells ({telemetry.neighborCells.nr5g.length})
            </h4>
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th><th>ARFCN</th><th>PCI</th><th>RSRP (dBm)</th>
                    <th>RSRQ (dB)</th><th>PLMN</th><th>CGI</th>
                  </tr>
                </thead>
                <tbody>
                  {telemetry.neighborCells.nr5g.length === 0 ? (
                    <tr><td colSpan={7} className="table-empty">No NR5G neighbor cells</td></tr>
                  ) : (
                    telemetry.neighborCells.nr5g.map((cell) => (
                      <tr key={cell.num}>
                        <td>{cell.num}</td><td>{cell.arfcn}</td><td>{cell.pci}</td>
                        <td>{cell.rsrp}</td><td>{cell.rsrq}</td>
                        <td>{cell.plmn}</td><td>{cell.cgi || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ── Network Context ─────────────────────────────────────── */}
          <Card title="Network Context" collapsible>
            <div className="rt-detail-grid-2">
              <InfoItem label="RAT"       value={telemetry.networkContext.rat} />
              <InfoItem label="MCC"       value={telemetry.networkContext.mcc} />
              <InfoItem label="MNC"       value={telemetry.networkContext.mnc} />
              <InfoItem label="CGI"       value={telemetry.networkContext.cgi} />
              {telemetry.networkContext.earfcn != null && <InfoItem label="EARFCN" value={telemetry.networkContext.earfcn} />}
              {telemetry.networkContext.arfcn  != null && <InfoItem label="ARFCN"  value={telemetry.networkContext.arfcn} />}
              <InfoItem label="Serving PCI" value={telemetry.networkContext.servingPci} />
              <InfoItem label="Latitude"    value={telemetry.networkContext.latitude} />
              <InfoItem label="Longitude"   value={telemetry.networkContext.longitude} />
              <InfoItem label="Timezone"    value={telemetry.networkContext.timezone} />
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function RefreshLabel({ label }) {
  if (!label) return null;
  return (
    <p className="rt-text-muted" style={{ fontSize: 12, marginBottom: 8, marginTop: 0 }}>
      {label}
    </p>
  );
}

export default RTTLive;
